import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtPrice, conditionLabel } from "@/lib/market";
import { Tag, Plus, MapPin, CheckCircle2, RotateCcw, Flame } from "lucide-react";

export default function MarketMine() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/marketplace/mine"); setItems(data || []); }
    catch (e) { setErr(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggleSold = async (id) => {
    try { await api.post(`/marketplace/${id}/mark-sold`); load(); }
    catch (e) { setErr(formatErr(e)); }
  };

  if (!user) return <div className="max-w-md mx-auto p-10"><p>Please sign in.</p></div>;

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-8 py-10" data-testid="market-mine-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline">Selling · Your listings</p>
          <h1 className="h-display text-4xl">Your for-sale items.</h1>
        </div>
        <Link to="/market/new" className="btn-primary" data-testid="market-mine-post"><Plus size={14}/> List an item</Link>
      </div>
      {err && <p className="text-sm text-[#B84B4B] mt-3">{err}</p>}

      <div className="grid sm:grid-cols-2 gap-4 mt-7" data-testid="market-mine-list">
        {items === null && <p className="text-[#8A8A8A]">Loading…</p>}
        {items && items.length === 0 && (
          <div className="card p-8 text-center sm:col-span-2">
            <Tag size={26} className="mx-auto text-[#0057FF] mb-2"/>
            <p className="text-[#4A4A4A]">No listings yet. Sell your first item to a Hooman.</p>
          </div>
        )}
        {(items || []).map(i => (
          <div key={i.id} className="card overflow-hidden" data-testid={`mine-listing-${i.id}`}>
            <Link to={`/market/${i.id}`} className="block">
              <div className="aspect-[4/3] bg-[#F2F2F0]">
                {i.images?.[0] ? <img src={i.images[0]} alt="" className="w-full h-full object-cover"/> :
                  <div className="w-full h-full flex items-center justify-center text-[#8A8A8A]"><Tag size={22}/></div>}
              </div>
            </Link>
            <div className="p-4">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Link to={`/market/${i.id}`} className="font-medium leading-tight truncate flex-1 hover:underline">{i.title}</Link>
                {i.is_boosted && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0057FF] text-white inline-flex items-center gap-1"><Flame size={9}/> {i.boost_tier}</span>}
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${i.status === "sold" ? "bg-[#0A0A0A] text-white" : "bg-[#F2F2F0] text-[#4A4A4A]"}`}>{i.status}</span>
              </div>
              <p className="text-sm text-[#4A4A4A] mt-1">{i.category} · {conditionLabel(i.condition)}</p>
              <div className="flex items-center justify-between mt-2">
                <p className="font-medium text-lg">{fmtPrice(i)}</p>
                <span className="text-xs text-[#8A8A8A] inline-flex items-center gap-1"><MapPin size={11}/> {i.location || "—"}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={()=>toggleSold(i.id)} className="btn-ghost text-xs" data-testid={`mine-toggle-sold-${i.id}`}>
                  {i.status === "sold" ? <><RotateCcw size={12}/> Relist</> : <><CheckCircle2 size={12}/> Mark sold</>}
                </button>
                <Link to={`/market/new?edit=${i.id}`} className="btn-ghost text-xs" data-testid={`mine-edit-${i.id}`}>Edit</Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
